import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { formatNumber, t } from 'ember-intl';
import type { ParityInsightSegmentationItem } from 'lumen/models';
import {
  type FixedScrollHeader,
  fixedScrollHeader,
} from 'lumen/utils/decorators/fixed-scroll-header';
import { calculateParityAnalyticsValues } from 'lumen/utils/parity-analytics/calculate-parity-analytics-values';
import roundParityPercentages from 'lumen/utils/parity-analytics/round-parity-percentages';

type Percentages = {
  loss: number;
  meet: number;
  win: number;
};

interface ParityInsightFixedKpiHeaderSignature {
  Args: {
    data?: ParityInsightSegmentationItem;
    hideHotelsMetric?: boolean;
  };
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
interface ParityInsightFixedKpiHeader
  extends FixedScrollHeader, Component<ParityInsightFixedKpiHeaderSignature> {}

// 660px is actual scrollTop at which the metric blocks will fully disappear from the screen. We subtract 40px to make the fixed header appear a bit earlier
@fixedScrollHeader({ scrollTopTrigger: 620 })
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class ParityInsightFixedKpiHeader extends Component<ParityInsightFixedKpiHeaderSignature> {
  @service declare intl: Services['intl'];

  get percentages(): Percentages {
    const { data } = this.args;

    if (data) {
      const percentages = calculateParityAnalyticsValues(data.value);
      const { loss, meet, win } = roundParityPercentages(percentages);

      return { loss, meet, win };
    }

    return { loss: 0, meet: 0, win: 0 };
  }

  get totalNbOfHotels(): string {
    const { data } = this.args;

    if (data?.value?.subscriptions) {
      return this.intl.formatNumber(data.value.subscriptions);
    }

    return '-';
  }

  get totalNbOfShops(): string {
    const { data } = this.args;

    if (data) {
      const percentages = calculateParityAnalyticsValues(data.value);

      return this.intl.formatNumber(percentages.totalNbOfShops);
    }

    return '-';
  }

  <template>
    <div
      class="pa-perf-metric-sticky-header-wrapper text-300--regular
        {{if this.showHeader 'visible'}}"
    >
      <div class="pa-perf-metric-sticky-header-content">
        <div class="header-item loss">
          <span class="kpi-name">
            {{t "parity_analytics.loss"}}
          </span>
          <span class="kpi-value">
            <span class="highlight">
              {{formatNumber this.percentages.loss style="percent"}}
            </span>
          </span>
        </div>

        <div class="header-item meet">
          <span class="kpi-name">
            {{t "parity_analytics.meet"}}
          </span>
          <span class="kpi-value">
            <span class="highlight">
              {{formatNumber this.percentages.meet style="percent"}}
            </span>
          </span>
        </div>

        <div class="header-item win">
          <span class="kpi-name">
            {{t "parity_analytics.win"}}
          </span>
          <span class="kpi-value">
            <span class="highlight">
              {{formatNumber this.percentages.win style="percent"}}
            </span>
          </span>
        </div>

        {{#unless @hideHotelsMetric}}
          <div class="header-item">
            <span class="kpi-name">
              {{t "general.hotels"}}
            </span>
            <span class="kpi-value">
              <span class="highlight">
                {{this.totalNbOfHotels}}
              </span>
            </span>
          </div>
        {{/unless}}

        <div class="header-item">
          <span class="kpi-name">
            {{t "parity_analytics.number_of_shops"}}
          </span>
          <span class="kpi-value">
            <span class="highlight">
              {{this.totalNbOfShops}}
            </span>
          </span>
        </div>
      </div>
    </div>
  </template>
}

export default ParityInsightFixedKpiHeader;
